import config from '../config';
import _ from 'lodash';

/**
 * A collection of product code strings for the Regular Saver Exclusive
 * Savings Account. This product requires us to display different content
 * in the Customer's Accounts list, in order to correctly explain details
 * about their products Interest Rate on deposits. This differs from the
 * standard way of displaying Savings Account information.
 * @see ProductUtils.isRegularSaverProductCode
 * @see /AccountDetailsNew/PanelData.jsx
 */
export const REGULAR_SAVER_EXCLUSIVE_PRODUCT_CODES = [
  '760', // Original Regular Saver Exclusive -> IM760
  //'631' is reserved – Unused.
  '632', // Regular Saver Exclusive (Issue 2) -> IM632
  '633', // Regular Saver Exclusive (Issue 3) -> IM633
  '634', // Regular Saver Exclusive (Issue 4) -> IM634
  '635', // Regular Saver Exclusive (Issue 5) -> IM635
  '636', // Regular Saver Exclusive (Issue 6) -> IM676
  '637', // Regular Saver Exclusive (Issue 7) -> IM637
  '638', // Regular Saver Exclusive (Issue 8) -> IM638
  '639', // Regular Saver Exclusive (Issue 9) -> IM639
];

/**
 * Private method to get the object for a particular product.
 * Log an error if not found.
 *
 * @param  {String} productCode  What product is the user looking for?
 * @return {Object}              Data, or empty object if none found (allows
 *                                  public utils to return a sensible default easily)
 */
const getProduct = (productCode) => {
  const result = config.productData ? config.productData[productCode] : {};

  if (process.env.NODE_ENV === 'development') {
    if (!productCode || !result) {
      console.error(`No product found for code: ${productCode}`);
    }
  }

  return result || {};
};

const ProductUtils = {
  getProduct,

  /**
   * Determine whether a given product code is a Regular Saver product,
   * which requires it's bespoke Interest Rate information to be displayed differently
   * to the customer in the Account Details view.
   * @param {string} productCode - e.g `'760'`
   * @returns {boolean}
   */
  isRegularSaverProductCode(productCode) {
    if (typeof productCode !== 'string') {
      throw new TypeError(
        "isRegularSaverProductCode() expected a string argument for `productCode` – e.g '632'. Received " +
          typeof productCode
      );
    }

    return REGULAR_SAVER_EXCLUSIVE_PRODUCT_CODES.includes(productCode);
  },

  isRegularSaverProductCode760(productCode) {
    if (typeof productCode !== 'string') {
      throw new TypeError(
        "isRegularSaverProductCode() expected a string argument for `productCode` – e.g '632'. Received " +
          typeof productCode
      );
    }

    return productCode === '760';
  },

  isRegularSaverProductCode632(productCode) {
    if (typeof productCode !== 'string') {
      throw new TypeError(
        "isRegularSaverProductCode() expected a string argument for `productCode` – e.g '632'. Received " +
          typeof productCode
      );
    }

    return productCode === '632';
  },

  getEligibilityQuestions(productCode) {
    return getProduct(productCode).eligibilityQuestions || [];
  },

  getOfferItems(productCode) {
    return getProduct(productCode).offerItems || [];
  },

  getOfferRestrictionsItems(productCode) {
    return getProduct(productCode).offerRestrictions || [];
  },

  getKeyFeatures(productCode) {
    return getProduct(productCode).keyFeatureItems || [];
  },

  getDocuments(productCode) {
    return getProduct(productCode).additionalDocumentItems || [];
  },

  getMonthlyFee(productCode) {
    return getProduct(productCode).monthlyFee;
  },

  getMinAge(productCode) {
    return getProduct(productCode).minimumAge;
  },

  getMaxAge(productCode) {
    return getProduct(productCode).maximumAge;
  },

  getName(productCode) {
    return getProduct(productCode).name;
  },

  getDescription(productCode) {
    return getProduct(productCode).description;
  },

  getLink(productCode) {
    return getProduct(productCode).productLink;
  },

  /**
   * Filter offers returning products which are saving related
   * @param {Array} offer items.
   * @return {Array} Of savings products elements.
   */
  getSavingAccountItems(offers) {
    if (_.isUndefined(offers) || _.isEmpty(offers)) {
      return null;
    }

    const lodashWrapper = _.runInContext();
    _.mixin(lodashWrapper, {
      chain: _.chain,
      first: _.first,
      thru: _.thru,
      filter: _.filter,
    });

    return lodashWrapper
      .chain(offers)
      .first()
      .thru((offer) => {
        return offer.products;
      })
      .filter((product) => {
        return product['product_type'] === 'SAVINGS ACCOUNT';
      })
      .value();
  },
};

export default ProductUtils;
